import React, { useCallback, useEffect, useState } from 'react';
import type { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import {
	CORE_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useDescriptionCompleterAnalytics } from '../common/analytics/index.tsx';
import { DescriptionCompleter } from './description-completer/index.tsx';
import type { Props } from './types.tsx';
import {
	isConfluenceLink,
	generatePayloadForProactiveDescriptionCompleter,
	triggerEditorStreamHubEvent,
} from './utils.tsx';
import messages from './messages.tsx';

export const AiProactiveDescriptionCompleter = (props: Props) => {
	const { formatMessage } = useIntl();
	const { editorApi, editorExperience, projectType, issueType, issueSummary } = props;
	const [showFooter, setShowFooter] = useState(false);
	const [currentCard, setCurrentCard] = useState<{ title?: string; url?: string } | undefined>();
	const [triggerType, setTriggerType] = useState<'paste' | 'click' | undefined>();

	const { fireTrack } = useDescriptionCompleterAnalytics();

	const onGenerateClick = useCallback(() => {
		triggerEditorStreamHubEvent(
			generatePayloadForProactiveDescriptionCompleter(currentCard?.url),
			editorExperience,
		);
		fireTrack('proactiveDescriptionCompleter clicked', 'descriptionCompleterFooter', {
			editorExperience,
			triggerType,
		});
	}, [currentCard?.url, editorExperience, fireTrack, triggerType]);

	useEffect(() => {
		// tracking the shared card state plugin
		// determines whether the AI suggest button should be shown or not
		const unsub = editorApi?.card?.sharedState.onChange(({ nextSharedState, prevSharedState }) => {
			if (prevSharedState?.cards.length === nextSharedState?.cards.length) {
				return;
			}
			if (prevSharedState?.cards.length !== 0 || nextSharedState?.cards.length !== 1) {
				setCurrentCard(undefined);
				return;
			}
			const [{ url, title }] = nextSharedState.cards;
			setCurrentCard({ url, title });
			setTriggerType('paste');
		});
		return unsub;
	}, [editorApi]);

	useEffect(() => {
		const unsub = editorApi?.selection?.sharedState.onChange(({ nextSharedState }) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const { node } = nextSharedState?.selection as NodeSelection;
			if (node === undefined || node.type.name !== 'inlineCard') {
				return;
			}
			const cardState = editorApi?.card?.sharedState.currentState();
			const card = cardState?.cards.find((item) => item.url === node.attrs.url);
			if (!card) {
				return;
			}
			const { url, title } = card;
			setCurrentCard({ url, title });
			setTriggerType('click');
		});
		return unsub;
	}, [editorApi]);

	useEffect(() => {
		const isInvalid = !currentCard?.url || !currentCard?.title || !issueSummary;
		if (isInvalid || !isConfluenceLink(currentCard?.url)) {
			setShowFooter(false);
			return;
		}

		const isTaskIssue = issueType === formatMessage(messages.taskIssueTypeName);
		const isBugIssue = issueType === formatMessage(messages.bugIssueTypeName);
		const isSoftwareProject = projectType === SOFTWARE_PROJECT;
		const isBusinessProject = projectType === CORE_PROJECT;

		// Hide footer to avoid stack/overlap with loom x-flow banner
		const isJwmLoomEligible = isBusinessProject && (isBugIssue || isTaskIssue);
		const isJswLoomEligible = isSoftwareProject && isBugIssue;

		if (editorExperience === 'IssueView' && (isJwmLoomEligible || isJswLoomEligible)) {
			fireTrack('proactiveDescriptionCompleter hidden', 'descriptionCompleterFooter', {
				editorExperience,
				triggerType,
			});
			setShowFooter(false);
			return;
		}

		setShowFooter(true);
		fireTrack('proactiveDescriptionCompleter initiated', 'descriptionCompleterFooter', {
			editorExperience,
			triggerType,
		});
	}, [
		currentCard,
		editorExperience,
		fireTrack,
		formatMessage,
		issueSummary,
		issueType,
		projectType,
		triggerType,
	]);

	const onClose = useCallback(() => {
		setShowFooter(false);
		setCurrentCard(undefined);
		fireTrack('proactiveDescriptionCompleter closed', 'descriptionCompleterFooter', {
			editorExperience,
			triggerType,
		});
	}, [editorExperience, fireTrack, triggerType]);

	return showFooter && <DescriptionCompleter onClick={onGenerateClick} onClose={onClose} />;
};
